import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UtilsService } from '../../../../shared/services/utils.service';
import { MatDividerModule } from '@angular/material/divider';
import {
	ClipsSizeResponse,
	CreateCategoryDetailsRequest,
	CreateCategoryRequest as CreateClippingCategoryRequest,
	CreateTagRequest,
	ManagementCategoryClippingControllerService,
	ManagementCategoryResponse,
	ManagementTagClippingControllerService,
	ManagementTagResponse,
	UpdateTagRequest,
} from 'api-clipping';
import { SharedService } from '../../../../shared/services/shared.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject } from 'rxjs/internal/Subject';
import { take } from 'rxjs';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { MatTabsModule } from '@angular/material/tabs';
import { Constants } from '../../../../shared/interfaces/constants';
import { CommonModule } from '@angular/common';
import { NgxColorsModule } from 'ngx-colors';
import {
	CreateCategoryRequest as CreateContractNoticeCategoryRequest,
	ManagementCategoryContractNoticesControllerService,
	ManagementTagContractNoticesControllerService,
	RelatedItemsSizeResponse,
} from 'api-contract-notices';
import {
	ManagementCreateCategoryRequest as CreateTranscriptionCategoryRequest,
	ManagementCategoryTranscriptionControllerService,
	ManagementTagTranscriptionControllerService,
	ManagementCreateTagRequest as CreateTranscriptionTagRequest,
	ManagementUpdateTagRequest as UpdateTranscriptionTagRequest,
	ManagementTranscriptionsSizeResponse,
} from 'api-transcription';
import { environment } from '../../../../../environments/environment';

interface InputData {
	mode: 'NEW' | 'EDIT' | 'DELETE' | 'DELETE_REASSIGN' | 'PENDING';
	type: string;
	typeWithScope: 'tag_clipping' | 'tag_contract_notices' | 'tag_transcriptions';
	item: ManagementCategoryResponse | ManagementTagResponse;
	additionalData: ManagementCategoryResponse[];
	preloadedData: any;
}
@Component({
	selector: 'app-tag',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatCardModule,
		MatButtonModule,
		MatTooltipModule,
		MatSelectModule,
		TranslateModule,
		MatCheckboxModule,
		MatProgressSpinnerModule,
		MatDividerModule,
		MatAutocompleteModule,
		MatTabsModule,
		NgxColorsModule,
		MatDialogModule,
	],
	templateUrl: './tag.component.html',
	styleUrl: './tag.component.scss',
})
export class TagComponent implements OnInit, OnDestroy {
	private onDestroy: Subject<any> = new Subject();
  externalAssets = environment.reseller.externalAssets;
	categories!: ManagementCategoryResponse[];
	preloadedData: any;
	filteredTags: ManagementTagResponse[] | undefined;
	itemRelatedClips = 0;

	private readonly CATEGORY_TYPES: Map<string, string> = new Map([
		['tag_clipping', 'CLIPPING'],
		['tag_contract_notices', 'CONTRACT_NOTICES'],
		['tag_transcriptions', 'TRANSCRIPTIONS'],
	]);
	categoryTypes: string[] = [];

	tabType: 'TAG' | 'CATEGORY' = 'TAG';

	formGroup: FormGroup = new FormGroup({});
	availableLanguages = Constants.AVAILABLE_LANGUAGES;
	avaliableLanguagesCodes = Constants.AVAILABLE_LANGUAGES.map(item => item.code);

	apiLoading = {
		management: false,
	};

	constructor(
		public dialogRef: MatDialogRef<TagComponent>,
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private fb: FormBuilder,
		public utils: UtilsService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private apiClippingCategory: ManagementCategoryClippingControllerService,
		private apiContractNoticesCategory: ManagementCategoryContractNoticesControllerService,
		private apiClippingTag: ManagementTagClippingControllerService,
		private apiContractNoticesTag: ManagementTagContractNoticesControllerService,
		private apiTranscriptionCategory: ManagementCategoryTranscriptionControllerService,
		private apiTranscriptionTag: ManagementTagTranscriptionControllerService
	) {
		this.categories = data.additionalData;
		this.preloadedData = data.preloadedData;

		switch (this.data.mode) {
			case 'NEW':
			case 'EDIT': {
				this.formGroup = this.fb.group({
					value: [, [Validators.required]],
				});
				this.avaliableLanguagesCodes.forEach(code => {
					let componentName = 'id' + this.utils.titleCase(code);
					let componentValue = this.data.item ? this.data.item.details?.find((detail: any) => detail.lang === code.toUpperCase())?.id : null;
					let control: FormControl = new FormControl();
					control = new FormControl(componentValue ? componentValue : '');
					this.formGroup.addControl(componentName, control);

					componentName = 'name' + this.utils.titleCase(code);
					componentValue = this.data.item ? this.data.item.details?.find((detail: any) => detail.lang === code.toUpperCase())?.name : null;
					control = new FormControl(componentValue ? componentValue : '', Validators.required);
					this.formGroup.addControl(componentName, control);
				});

				if (this.data.type === 'CATEGORY') {
					let control: FormControl = new FormControl(this.CATEGORY_TYPES.get(this.data.typeWithScope), Validators.required);
					this.formGroup.addControl('type', control);
				} else if (this.data.type === 'TAG') {
					let control: FormControl = new FormControl(this.data.item ? (this.data.item as ManagementTagResponse).categoryId : null, Validators.required);
					this.formGroup.addControl('categoryId', control);

					control = new FormControl(this.data.item ? (this.data.item as ManagementTagResponse).colorCode : '', Validators.required);
					this.formGroup.addControl('colorCode', control);

					control = new FormControl(this.data.item ? (this.data.item as any).fill : false);
					this.formGroup.addControl('fill', control);

					if (this.data.typeWithScope !== 'tag_transcriptions') {
						control = new FormControl(this.data.item ? (this.data.item as ManagementTagResponse).screenshot : false);
						this.formGroup.addControl('screenshot', control);

						control = new FormControl(this.data.item ? (this.data.item as ManagementTagResponse).langs : '');
						this.formGroup.addControl('langs', control);
					}
				}

				if (this.data.item) {
					if (this.preloadedData) {
              this.formGroup.reset();
							let componentValue = this.preloadedData ? this.preloadedData['categoryId'] : null;
							this.formGroup.controls['categoryId'].setValue(componentValue);
					} else {
						this.formGroup.patchValue(this.data.item);
					}
				}
				break;
			}
			case 'DELETE': {
				this.data.mode = 'PENDING';
				this.getRelatedClips();

				this.formGroup = this.fb.group({
					reassignTo: [],
					reassign: [false, [Validators.required]],
				});
				break;
			}
		}
	}

	ngOnInit(): void {
		if (this.formGroup.controls['reassignTo']) {
			this.formGroup.controls['reassignTo'].valueChanges.pipe(startWith(''), debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.search(searchTerm);
			});
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	private search(searchTerm: string) {
		if (searchTerm.length >= 1) {
			this.getActiveTagControler()
				.searchTags(searchTerm)
				.pipe(take(1))
				.subscribe((response: any) => {
					this.filteredTags = response.filter((tag: ManagementTagResponse) => tag.id !== this.data.item.id);
				});
		} else {
			this.filteredTags = [];
		}
	}

	displayFn(tag: ManagementTagResponse): string {
		return tag && tag.id ? this.utils.getParameterByLanguageScript(tag, 'name', true) : tag.value ?? '';
	}

	private getRelatedClips() {
		this.getActiveTagControler()
			.getTagRelatedToSize(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: (response: ClipsSizeResponse | RelatedItemsSizeResponse | ManagementTranscriptionsSizeResponse) => {
					this.itemRelatedClips = response.relatedItemsSize || 0;
					this.data.mode = this.itemRelatedClips ? 'DELETE_REASSIGN' : 'DELETE';
				},
			});
	}

	action() {
		this.data.type === 'CATEGORY' ? this.categoryAction() : this.tagAction();
	}

	private categoryAction() {
		this.apiLoading.management = true;
		switch (this.data.mode) {
			case 'NEW': {
				let categoryDTO: CreateClippingCategoryRequest | CreateContractNoticeCategoryRequest | CreateTranscriptionCategoryRequest = {
					value: this.formGroup.controls['value'].value,
					details: this.avaliableLanguagesCodes.map(langCode => ({
						name: this.formGroup.controls['name' + this.utils.titleCase(langCode)].value,
						lang: (<any>CreateCategoryDetailsRequest.LangEnum)[langCode.toUpperCase()],
					})),
					type: this.formGroup.controls['type'].value,
				};

				this.getActiveCategoryControler()
					.createCategory(this.utils.validateRequestObject(categoryDTO))
					.pipe(take(1))
					.subscribe({
						next: () => {
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
								type: 'SUCCESS',
							});
							this.closeDialog();
						},
						error: () => {
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
								type: 'ERROR',
							});
							this.apiLoading.management = false;
						},
					});
				break;
			}
			case 'EDIT': {
				const categoryDTO: any = this.data.item;
				categoryDTO.value = this.formGroup.controls['value'].value;
				categoryDTO.type = this.formGroup.controls['type'].value;
				categoryDTO.details?.forEach((detail: any) => {
					detail.name = this.formGroup.controls['name' + this.utils.titleCase(detail.lang?.toLowerCase() || '')].value;
				});

				this.getActiveCategoryControler()
					.updateCategory(this.utils.validateRequestObject(categoryDTO), this.data.item.id)
					.pipe(take(1))
					.subscribe({
						next: () => {
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
								type: 'SUCCESS',
							});
							this.closeDialog();
						},
						error: () => {
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
								type: 'ERROR',
							});
							this.apiLoading.management = false;
						},
					});
				break;
			}
			case 'DELETE': {
				this.getActiveCategoryControler()
					.deleteCategory(this.data.item.id)
					.pipe(take(1))
					.subscribe({
						next: () => {
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
								type: 'SUCCESS',
							});
							this.closeDialog();
						},
						error: () => {
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
								type: 'ERROR',
							});
							this.apiLoading.management = false;
						},
					});
			}
		}
	}

	tagAction() {
		this.apiLoading.management = true;
		switch (this.data.mode) {
			case 'NEW': {
				if (this.data.typeWithScope === 'tag_transcriptions') {
					let tagDTO: CreateTranscriptionTagRequest = {
						value: this.formGroup.controls['value'].value,
						colorCode: this.formGroup.controls['colorCode'].value,
						categoryId: this.formGroup.controls['categoryId'].value,
						details: this.avaliableLanguagesCodes.map(langCode => ({
							name: this.formGroup.controls['name' + this.utils.titleCase(langCode)].value,
							lang: (<any>CreateCategoryDetailsRequest.LangEnum)[langCode.toUpperCase()],
						})),
					};

					this.getActiveTagControler()
						.createTag(this.utils.validateRequestObject(tagDTO))
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
									type: 'SUCCESS',
								});
								this.closeDialog();
							},
							error: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
									type: 'ERROR',
								});
								this.apiLoading.management = false;
							},
						});
				} else {
					let tagDTO: CreateTagRequest = {
						value: this.formGroup.controls['value'].value,
						colorCode: this.formGroup.controls['colorCode'].value,
						fill: this.formGroup.controls['fill'].value,
						screenshot: this.formGroup.controls['screenshot'].value,
						categoryId: this.formGroup.controls['categoryId'].value,
						translate: this.formGroup.controls['langs'].value && this.formGroup.controls['langs'].value.length ? true : false,
						langs: this.formGroup.controls['langs'].value ? this.formGroup.controls['langs'].value : [],
						details: this.avaliableLanguagesCodes.map(langCode => ({
							name: this.formGroup.controls['name' + this.utils.titleCase(langCode)].value,
							lang: (<any>CreateCategoryDetailsRequest.LangEnum)[langCode.toUpperCase()],
						})),
					};

					this.getActiveTagControler()
						.createTag(this.utils.validateRequestObject(tagDTO))
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
									type: 'SUCCESS',
								});
								this.closeDialog();
							},
							error: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
									type: 'ERROR',
								});
								this.apiLoading.management = false;
							},
						});
				}
				break;
			}
			case 'EDIT': {
				if (this.data.typeWithScope === 'tag_transcriptions') {
					const tagDTO: UpdateTranscriptionTagRequest = {
						value: this.formGroup.controls['value'].value,
						colorCode: this.formGroup.controls['colorCode'].value,
						categoryId: this.formGroup.controls['categoryId'].value,
						details: this.avaliableLanguagesCodes.map(langCode => ({
							id: this.formGroup.controls['id' + this.utils.titleCase(langCode)].value,
							name: this.formGroup.controls['name' + this.utils.titleCase(langCode)].value,
							lang: (<any>UpdateTagRequest.LangsEnum)[langCode.toUpperCase()],
						})),
					};

					this.getActiveTagControler()
						.updateTag(this.utils.validateRequestObject(tagDTO), this.data.item.id)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
									type: 'SUCCESS',
								});
								this.closeDialog();
							},
							error: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
									type: 'ERROR',
								});
								this.apiLoading.management = false;
							},
						});
				} else {
					const tagDTO: UpdateTagRequest = {
						value: this.formGroup.controls['value'].value,
						colorCode: this.formGroup.controls['colorCode'].value,
						fill: this.formGroup.controls['fill'].value,
						screenshot: this.formGroup.controls['screenshot'].value,
						categoryId: this.formGroup.controls['categoryId'].value,
						translate: this.formGroup.controls['langs'].value && this.formGroup.controls['langs'].value.length ? true : false,
						langs: this.formGroup.controls['langs'].value,
						details: this.avaliableLanguagesCodes.map(langCode => ({
							id: this.formGroup.controls['id' + this.utils.titleCase(langCode)].value,
							name: this.formGroup.controls['name' + this.utils.titleCase(langCode)].value,
							lang: (<any>UpdateTagRequest.LangsEnum)[langCode.toUpperCase()],
						})),
					};

					this.getActiveTagControler()
						.updateTag(this.utils.validateRequestObject(tagDTO), this.data.item.id)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
									type: 'SUCCESS',
								});
								this.closeDialog();
							},
							error: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
									type: 'ERROR',
								});
								this.apiLoading.management = false;
							},
						});
				}
				break;
			}
			case 'DELETE_REASSIGN':
			case 'DELETE': {
				const reassignTag: ManagementTagResponse = this.formGroup.controls['reassignTo'].value;
				this.getActiveTagControler()
					.deleteTag(this.data.item.id, reassignTag && reassignTag.id ? reassignTag.id : '')
					.pipe(take(1))
					.subscribe({
						next: () => {
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
								type: 'SUCCESS',
							});
							this.closeDialog();
						},
						error: (err: any) => {
							this.sharedService.showNotification.next({
								message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
								type: 'ERROR',
							});
							this.apiLoading.management = false;
						},
					});
			}
		}
	}

	private getActiveCategoryControler() {
		switch (this.data.typeWithScope) {
			case 'tag_clipping':
				return this.apiClippingCategory as any;
			case 'tag_contract_notices':
				return this.apiContractNoticesCategory as any;
			case 'tag_transcriptions':
				return this.apiTranscriptionCategory as any;
			default:
				return this.apiClippingCategory;
		}
	}

	private getActiveTagControler() {
		switch (this.data.typeWithScope) {
			case 'tag_clipping':
				return this.apiClippingTag;
			case 'tag_contract_notices':
				return this.apiContractNoticesTag;
			case 'tag_transcriptions':
				return this.apiTranscriptionTag;
			default:
				return this.apiClippingTag;
		}
	}

	closeDialog() {
		this.dialogRef.close();
	}
}
